import type React from "react";
import { useTranslation } from "react-i18next";
import TodoContainer from "../organisms/TodoContainer";
import UpcomingWorkflowsContainer from "../organisms/UpcomingWorkflowsContainer";
import PublishReportContainer from "../organisms/PublishReportContainer";
import { PinnedDocumentsContainer } from "../organisms/pinned/PinnedDocumentsContainer";
import { useUserContext } from "../hooks/useUserContext";
import { PlatformRole } from "../config/types";
import { useLocation, useNavigate, Link } from "react-router";
import { NewDocumentButton } from "~/molecules/NewDocumentButton";
import OngoingWorkflowsContainer from "../organisms/OngoingWorkflowsContainer";
import { DocumentConfetti } from "~/molecules/DocumentConfetti";
import { TestErrorButton } from "~/molecules/TestErrorButton";
import { Button } from "~/atoms/button";
import { Card } from "~/atoms/card";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";

type LocationState = { showConfetti?: boolean } | null;

const Dashboard: React.FC = () => {
  const { user } = useUserContext();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const showPinnedDocuments = useFeatureFlagEnabled(
    featureFlags.pinnedDocuments,
  );

  const isReader = user.role === PlatformRole.reader;
  const state = location.state as LocationState;
  const showTestError =
    new URLSearchParams(location.search).get("testError") === "true";
  const isSectionsEnabled =
    useFeatureFlagEnabled(featureFlags.genestaSections) && !isReader;

  return (
    <div className="container flex flex-col gap-6 py-6">
      <DocumentConfetti
        show={state?.showConfetti ?? false}
        onComplete={() => navigate("/", { state: { showConfetti: false } })}
      />
      {isReader ? (
        <>
          {showPinnedDocuments && <PinnedDocumentsContainer />}
          <TodoContainer />
        </>
      ) : (
        <>
          <div className="flex justify-between">
            <div className="flex gap-2">
              <NewDocumentButton />
              {isSectionsEnabled && (
                <Button variant="outline" asChild>
                  <Link to="/sections">
                    {t(($) => $.commons.sections.viewSections)}
                  </Link>
                </Button>
              )}
            </div>
            <Card className="flex justify-center gap-2 overflow-auto px-3 py-1">
              <p className="text-sm">
                {t(($) => $.dashboard.userManual.getStarted)}{" "}
                <Link
                  className="text-primary cursor-pointer font-medium hover:underline"
                  to="/user-guide"
                >
                  {t(($) => $.dashboard.userManual.userGuide)}
                </Link>
              </p>
            </Card>
            {showTestError && <TestErrorButton />}
          </div>
          {showPinnedDocuments && <PinnedDocumentsContainer />}
          <TodoContainer />
          <div className="contents xl:grid xl:grid-cols-2 xl:gap-6">
            <OngoingWorkflowsContainer />
            <UpcomingWorkflowsContainer />
          </div>
          <PublishReportContainer />
        </>
      )}
    </div>
  );
};

export default Dashboard;
